import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Search, UserMinus, User, Users, Loader2, Eye, Check, UserPlus } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Unregistered = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const basePath = location.pathname.startsWith('/manager') ? '/manager' : '/admin';

    const [tab, setTab] = useState('students');
    const [students, setStudents] = useState([]);
    const [guardians, setGuardians] = useState([]);
    const [programs, setPrograms] = useState([]);
    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');

    const [selectedStudent, setSelectedStudent] = useState(null);
    const [approveDialog, setApproveDialog] = useState(false);
    const [selectedProgramId, setSelectedProgramId] = useState('');
    const [selectedClassId, setSelectedClassId] = useState('');
    const [processing, setProcessing] = useState(false);

    useEffect(() => { fetchData(); }, []);

    const fetchData = async () => {
        try {
            const [studentsRes, guardiansRes, programsRes, classesRes] = await Promise.all([
                axios.get(`${API_URL}/api/unregistered`, { withCredentials: true }),
                axios.get(`${API_URL}/api/unregistered/guardians`, { withCredentials: true }),
                axios.get(`${API_URL}/api/programs`, { withCredentials: true }),
                axios.get(`${API_URL}/api/classes`, { withCredentials: true })
            ]);
            setStudents(studentsRes.data);
            setGuardians(guardiansRes.data);
            setPrograms(programsRes.data);
            setClasses(classesRes.data);
        } catch { toast.error('Failed to load unregistered list'); }
        finally { setLoading(false); }
    };

    const openApprove = (student) => {
        setSelectedStudent(student);
        setSelectedProgramId(student.program_id || '');
        setSelectedClassId('');
        setApproveDialog(true);
    };

    const handleMoveToWaitingList = async (student) => {
        if (!window.confirm(`Move ${student.first_name} ${student.last_name} to the waiting list?`)) return;
        try {
            await axios.put(`${API_URL}/api/students/${student.id}/status`, { status: 'waiting_list' }, { withCredentials: true });
            toast.success('Moved to waiting list');
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to move student');
        }
    };

    const handleEnroll = async (student) => {
        if (!window.confirm(`Enrol ${student.first_name} ${student.last_name}?\n\nThe guardian will receive an email + notification with a Stripe payment link for the €100 enrolment fee. The student is fully active only after the fee is paid.`)) return;
        try {
            // If unregistered, flip to waiting_list first (approve endpoint requires it)
            if (student.status !== 'waiting_list') {
                await axios.put(`${API_URL}/api/students/${student.id}/status`, { status: 'waiting_list' }, { withCredentials: true });
            }
            const r = await axios.post(`${API_URL}/api/waiting-list/approve`, {
                student_id: student.id,
                class_id: null,
            }, { withCredentials: true });
            toast.success(`Enrolment link (€${r.data.fee}) emailed to guardian`);
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to enrol');
        }
    };

    const handleApprove = async () => {
        if (!selectedClassId) { toast.error('Please select a class'); return; }
        setProcessing(true);
        try {
            if (selectedProgramId && selectedProgramId !== selectedStudent.program_id) {
                await axios.put(`${API_URL}/api/students/${selectedStudent.id}`, { program_id: selectedProgramId }, { withCredentials: true });
            }
            await axios.post(`${API_URL}/api/unregistered/approve`, {
                student_id: selectedStudent.id, class_id: selectedClassId
            }, { withCredentials: true });
            toast.success('Student activated & enrolled');
            setApproveDialog(false); setSelectedStudent(null); setSelectedClassId(''); setSelectedProgramId('');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to enrol');
        } finally { setProcessing(false); }
    };

    const getProgram = (id) => programs.find(p => p.id === id);
    const getProgramClasses = (programId) => classes.filter(c => c.program_id === programId);

    const normalizeMobile = (m) => (m || '').replace(/[^0-9+]/g, '');
    const q = searchQuery.trim().toLowerCase();
    const qDigits = q.replace(/[^0-9+]/g, '');
    const mobileMatch = (mobiles) => qDigits && mobiles.some(m => normalizeMobile(m).includes(qDigits));

    const filteredStudents = students.filter(s =>
        !q || (s.first_name || '').toLowerCase().includes(q) ||
        (s.last_name || '').toLowerCase().includes(q) ||
        (s.reg_code || '').toLowerCase().includes(q) ||
        mobileMatch([s.parent?.mobile, s.parent?.guardian2_mobile])
    );
    const filteredGuardians = guardians.filter(g =>
        !q || (g.first_name || '').toLowerCase().includes(q) ||
        (g.last_name || '').toLowerCase().includes(q) ||
        (g.reg_code || '').toLowerCase().includes(q) ||
        (g.email || '').toLowerCase().includes(q) ||
        mobileMatch([g.mobile, g.guardian2_mobile])
    );

    if (loading) {
        return <DashboardLayout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#064E3B]"></div></div></DashboardLayout>;
    }

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="unregistered-page">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">Unregistered</h1>
                        <p className="text-[#4B5563]">Students not currently enrolled — click a student to enrol them</p>
                    </div>
                    <Badge variant="secondary" className="w-fit"><UserMinus className="h-4 w-4 mr-1" />{students.length} unregistered</Badge>
                </div>

                <Card className="card-surface"><CardContent className="p-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4B5563]" />
                        <Input placeholder="Search by name, email, mobile or reg code..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" data-testid="unregistered-search" />
                    </div>
                </CardContent></Card>

                <Tabs value={tab} onValueChange={setTab} className="space-y-4">
                    <TabsList>
                        <TabsTrigger value="guardians" data-testid="tab-unreg-guardians"><Users className="h-4 w-4 mr-2" />Guardians ({guardians.length})</TabsTrigger>
                        <TabsTrigger value="students" data-testid="tab-unreg-students"><User className="h-4 w-4 mr-2" />Students ({students.length})</TabsTrigger>
                    </TabsList>

                    <TabsContent value="guardians">
                        <Card className="card-surface"><CardContent className="p-0">
                            {filteredGuardians.length > 0 ? (
                                <Table>
                                    <TableHeader><TableRow>
                                        <TableHead>Code</TableHead><TableHead>Guardian</TableHead><TableHead>Email</TableHead><TableHead>Mobile</TableHead><TableHead>Unregistered Students</TableHead><TableHead className="text-right">Actions</TableHead>
                                    </TableRow></TableHeader>
                                    <TableBody>
                                        {filteredGuardians.map(g => (
                                            <TableRow key={g.id} className="cursor-pointer hover:bg-[#FAF9F6]" onClick={() => navigate(`${basePath}/parents/${g.id}`)} data-testid={`unreg-guardian-row-${g.id}`}>
                                                <TableCell><Badge variant="outline" className="font-mono tabular-nums">{g.reg_code}</Badge></TableCell>
                                                <TableCell className="font-medium">{g.first_name} {g.last_name}</TableCell>
                                                <TableCell className="text-sm">{g.email}</TableCell>
                                                <TableCell className="text-sm">{g.mobile}</TableCell>
                                                <TableCell><div className="flex items-center gap-2"><Badge className="bg-[#6B7280]">{g.students_count}</Badge><span className="text-xs text-[#4B5563] line-clamp-1">{(g.student_names || []).join(', ')}</span></div></TableCell>
                                                <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); navigate(`${basePath}/parents/${g.id}`); }}><Eye className="h-4 w-4" /></Button></TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            ) : <div className="flex flex-col items-center justify-center py-12"><Users className="h-12 w-12 text-[#4B5563]/30 mb-4" /><p className="text-[#4B5563]">No guardians with unregistered students</p></div>}
                        </CardContent></Card>
                    </TabsContent>

                    <TabsContent value="students">
                        <Card className="card-surface"><CardContent className="p-0">
                            {filteredStudents.length > 0 ? (
                                <Table>
                                    <TableHeader><TableRow>
                                        <TableHead>Student</TableHead><TableHead>Parent</TableHead><TableHead>Last Program</TableHead><TableHead>Date of Birth</TableHead><TableHead className="text-right">Actions</TableHead>
                                    </TableRow></TableHeader>
                                    <TableBody>
                                        {filteredStudents.map(student => (
                                            <TableRow key={student.id} className="hover:bg-[#FAF9F6]" data-testid={`unreg-student-row-${student.id}`}>
                                                <TableCell>
                                                    <div className="flex items-center space-x-3">
                                                        <div className="h-10 w-10 rounded-full bg-[#6B7280]/10 flex items-center justify-center"><User className="h-5 w-5 text-[#6B7280]" /></div>
                                                        <div>
                                                            <p className="font-medium text-[#1F2937]">{student.first_name} {student.last_name}</p>
                                                            <p className="text-xs text-[#4B5563] tabular-nums">{student.reg_code}</p>
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell>{student.parent && <div><p className="text-sm">{student.parent.first_name} {student.parent.last_name}</p><p className="text-xs text-[#4B5563]">{student.parent.email}</p></div>}</TableCell>
                                                <TableCell><Badge variant="outline">{getProgram(student.program_id)?.name || '—'}</Badge></TableCell>
                                                <TableCell className="tabular-nums">{student.date_of_birth}</TableCell>
                                                <TableCell className="text-right">
                                                    <div className="flex justify-end gap-1.5">
                                                        <Button size="sm" variant="outline" className="text-[#F59E0B] border-[#F59E0B]/40 hover:bg-[#F59E0B]/10" onClick={() => handleMoveToWaitingList(student)} data-testid={`move-to-waiting-${student.id}`}>
                                                            <UserPlus className="h-4 w-4 mr-1" />To waiting list
                                                        </Button>
                                                        <Button size="sm" variant="outline" className="text-[#10B981] border-[#10B981]/40 hover:bg-[#10B981]/10" onClick={() => handleEnroll(student)} data-testid={`enrol-${student.id}`}>
                                                            <Check className="h-4 w-4 mr-1" />Enrol (€100)
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            ) : <div className="flex flex-col items-center justify-center py-12"><UserMinus className="h-12 w-12 text-[#4B5563]/30 mb-4" /><p className="text-[#4B5563]">No unregistered students</p></div>}
                        </CardContent></Card>
                    </TabsContent>
                </Tabs>

                <Dialog open={approveDialog} onOpenChange={setApproveDialog}>
                    <DialogContent>
                        <DialogHeader><DialogTitle className="font-['Outfit']">Enrol Unregistered Student</DialogTitle></DialogHeader>
                        <div className="space-y-4 py-4">
                            {selectedStudent && (
                                <div className="p-4 bg-[#FAF9F6] rounded-lg space-y-1">
                                    <p className="font-medium">{selectedStudent.first_name} {selectedStudent.last_name}</p>
                                    <p className="text-xs text-[#4B5563]">Reg code: {selectedStudent.reg_code} · DOB: {selectedStudent.date_of_birth}</p>
                                </div>
                            )}
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Program</label>
                                <Select value={selectedProgramId} onValueChange={(v) => { setSelectedProgramId(v); setSelectedClassId(''); }}>
                                    <SelectTrigger data-testid="unreg-program-select"><SelectValue placeholder="Select a program" /></SelectTrigger>
                                    <SelectContent>
                                        {programs.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Assign to Class</label>
                                <Select value={selectedClassId} onValueChange={setSelectedClassId} disabled={!selectedProgramId}>
                                    <SelectTrigger data-testid="unreg-class-select"><SelectValue placeholder={selectedProgramId ? "Select a class" : "Pick a program first"} /></SelectTrigger>
                                    <SelectContent>
                                        {getProgramClasses(selectedProgramId).map(cls => (<SelectItem key={cls.id} value={cls.id}>{cls.arabic_name || cls.english_name || cls.name} {cls.room ? `(${cls.room})` : ''}</SelectItem>))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setApproveDialog(false)}>Cancel</Button>
                            <Button onClick={handleApprove} disabled={processing} className="btn-primary" data-testid="confirm-unreg-enrol">{processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Enrol</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </DashboardLayout>
    );
};

export default Unregistered;
